import { mapOrd, rawOrd } from '@lc/libarray';
import { batch, enumerate, map } from '@lc/libiter';
import { datesIsCalString, datesIsCalWeekString, datesIsDateRanges, } from './guards.js';
/**
 * Compute the week number of a given date
 */
export function weekNo(date) {
    date = new Date(date);
    date.setHours(12);
    const jan1st = new Date(`${date.getUTCFullYear().toString().padStart(4, '0')}-01-01`);
    const day1 = new Date(jan1st.toISOString());
    while (day1.getUTCDay() !== 1)
        day1.setUTCDate(day1.getUTCDate() + 1);
    const delta = date.getTime() - day1.getTime();
    const deltaDays = delta / (1000 * 60 * 60 * 24);
    return Math.ceil(deltaDays / 7);
}
// TODO: unit test this, especially around new year
/**
 * Compute a date for a given year and week number
 */
export function dateForWeek(year, no) {
    const attempt = new Date(`${year.toString().padStart(4, '0')}-01-01`);
    attempt.setTime(attempt.getTime() + (no - 1) * 7 * 24 * 60 * 60 * 1000);
    attempt.setUTCHours(12);
    while (attempt.getUTCDay() !== 1) {
        attempt.setUTCDate(attempt.getUTCDate() - 1);
    }
    if (attempt.getUTCFullYear() < year)
        attempt.setUTCDate(attempt.getUTCDate() + 7);
    const wn = weekNo(attempt);
    if (wn === no)
        return attempt.toISOString().split('T')[0];
    else
        attempt.setUTCDate(attempt.getUTCDate() + 7 * (no - wn));
    return attempt.toISOString().split('T')[0];
}
/**
 * Compute the date of the Monday of the given date, and how far apart they are
 */
export function getMondayAndDelta(date) {
    date = new Date(date);
    date.setUTCHours(12);
    let delta = 0;
    while (date.getUTCDay() !== 1) {
        date.setUTCDate(date.getUTCDate() - 1);
        delta++;
    }
    return [date.toISOString().split('T')[0], delta];
}
/**
 * Function to get the Monday of the week of the given date
 */
export function getMonday(date) {
    return getMondayAndDelta(date)[0];
}
/**
 * Add a given amount of days to the given date
 */
export function addDays(date, days) {
    date = new Date(date);
    date.setUTCHours(12);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().split('T')[0];
}
/**
 * Compute the difference (in days) between two dates
 */
export function deltaDays(a, b) {
    a = new Date(a);
    b = new Date(b);
    return Math.floor((b.getTime() - a.getTime()) / 1000 / 60 / 60 / 24 + 0.5);
}
/**
 * Iterate all dates between the two parameters, inclusive
 */
export function* iterDatesAB(start, end) {
    if (start instanceof Date)
        start = start.toISOString();
    if (end instanceof Date)
        end = end.toISOString();
    if (start > end)
        throw new Error('Cannot iterate dates backwards');
    let date = start;
    while (date < end) {
        yield date;
        const dt = new Date(date);
        dt.setUTCDate(dt.getUTCDate() + 1);
        date = dt.toISOString().split('T')[0];
    }
    yield date;
}
/**
 * Iterate all dates in a {@link CalString}
 */
export function* iterCalString(start, calString) {
    // eslint-disable-next-line unicorn/no-for-loop
    for (let i = 0; i < calString.length; i++) {
        // eslint-disable-next-line security/detect-object-injection
        if (+calString[i])
            yield addDays(start, i);
    }
}
/**
 * Iterate all dates in a {@link CalWeekString}
 */
export function* iterCalWeekString(calWeekString) {
    for (const [k, v] of Object.entries(calWeekString).sort(mapOrd(rawOrd, x => x[0]))) {
        for (const date of iterCalString(k, v))
            yield date;
    }
}
/**
 * Checks if a {@link CalString} contains a given date
 */
export function calStringHas(start, calString, date) {
    const delta = deltaDays(start, date);
    if (delta < 0)
        return false;
    if (delta >= calString.length)
        return false;
    // eslint-disable-next-line security/detect-object-injection
    return !!+calString[delta];
}
/**
 * Checks if a {@link CalWeekString} contains a given date
 */
export function calWeekStringHas(calWeekString, date) {
    const [monday, delta] = getMondayAndDelta(date);
    // eslint-disable-next-line security/detect-object-injection
    return !!+calWeekString[monday]?.[delta];
}
function min(a, b) {
    // eslint-disable-next-line unicorn/prefer-math-min-max
    return a < b ? a : b;
}
function max(a, b) {
    // eslint-disable-next-line unicorn/prefer-math-min-max
    return a > b ? a : b;
}
/**
 * Iterate all dates in a given {@link DateRanges}
 */
export function* iterDateRanges(ranges) {
    ranges = [...ranges].sort(mapOrd(rawOrd, x => {
        if ('date' in x)
            return x.date;
        return x.start;
    }));
    let lastDate = '0000-00-00';
    for (const range of ranges) {
        if ('date' in range) {
            if (range.date > lastDate) {
                yield range.date;
                lastDate = range.date;
            }
        }
        else {
            if (lastDate >= range.end)
                continue;
            for (const date of iterDatesAB(max(lastDate, range.start), range.end)) {
                if (date > lastDate) {
                    yield date;
                    lastDate = date;
                }
            }
        }
    }
}
/**
 * Function that return the start date in a range of dates
 */
function dateRangeStart(range) {
    if ('date' in range)
        return range.date;
    return range.start;
}
/**
 * Function that return the end date in a range of dates
 */
function dateRangeEnd(range) {
    if ('date' in range)
        return range.date;
    return range.end;
}
/**
 * Minify a {@link DateRanges} object to optimize its size
 */
export function simplifyDateRanges(ranges) {
    if (!ranges.length)
        return [];
    if (ranges.length === 1) {
        if ('date' in ranges[0])
            return ranges;
        if (ranges[0].start === ranges[0].end)
            return [{ date: ranges[0].start }];
        return ranges;
    }
    ranges = [...ranges].sort(mapOrd(rawOrd, x => {
        if ('date' in x)
            return x.date;
        return x.start;
    }));
    for (let i = 0; i < ranges.length;) {
        // eslint-disable-next-line security/detect-object-injection
        let range = ranges[i];
        // concatenation with previous range
        if (i) {
            const prevEnd = dateRangeEnd(ranges[i - 1]);
            const thisStart = dateRangeStart(range);
            if (deltaDays(prevEnd, thisStart) <= 1) {
                ranges.splice(i, 1);
                const start = min(dateRangeStart(ranges[i - 1]), thisStart);
                const end = max(prevEnd, dateRangeEnd(range));
                ranges[i - 1] = start === end ? { date: start } : { start, end };
                continue;
            }
        }
        // local simplification
        if ('start' in range && range.start === range.end) {
            // eslint-disable-next-line security/detect-object-injection
            range = ranges[i] = { date: range.start };
        }
        i++;
    }
    return ranges;
}
/**
 * Check if a {@link DateRanges} contains the given date
 */
export function dateRangeHas(range, date) {
    if (date instanceof Date)
        date = date.toISOString().split('T')[0];
    if ('date' in range)
        return range.date === date;
    return range.start <= date && range.end >= date;
}
/**
 * Check if a {@link DateRange} is before (-1), overlapping (0) or after (1) a given date
 */
export function dateRangeCompare(range, date) {
    if (date instanceof Date)
        date = date.toISOString().split('T')[0];
    if ('date' in range) {
        if (range.date < date)
            return -1;
        if (range.date === date)
            return 0;
        return 1;
    }
    else {
        if (range.start < date)
            return -1;
        if (range.end <= date)
            return 0;
        return 1;
    }
}
/**
 * Check if a {@link DateRange} contains a given date
 */
export function dateRangesHas(ranges, date, simplified) {
    if (!simplified)
        ranges = simplifyDateRanges(ranges);
    if (date instanceof Date)
        date = date.toISOString().split('T')[0];
    const len = ranges.length;
    const vLen = Math.pow(2, Math.ceil(Math.log2(len)));
    let i = vLen / 2;
    let interval = vLen / 4;
    while (interval >= 1) {
        if (i >= len) {
            i -= interval;
            interval /= 2;
            continue;
        }
        const range = ranges[Math.floor(i)];
        const cmp = dateRangeCompare(range, date);
        if (!cmp)
            return true;
        i += interval * -cmp;
        interval /= 2;
    }
    return false;
}
/**
 * Convert a {@link CalString} to a {@link CalWeekString}
 */
export function calStringToCalWeekString(start, calString) {
    const [monday, delta] = getMondayAndDelta(start);
    calString = '0'.repeat(delta) + calString;
    return Object.fromEntries(map(enumerate(batch(calString, 7)), ([cs, i]) => [
        addDays(monday, i * 7),
        (cs.join('') + '0'.repeat(7 - cs.length)),
    ]));
}
/**
 * Convert a {@link CalWeekString} to a {@link CalString}
 */
export function calWeekStringToCalString(calWeekString) {
    const entries = Object.entries(calWeekString).sort(mapOrd(rawOrd, x => x[0]));
    if (!entries.length)
        return ['1970-01-01', ''];
    const start = entries[0][0];
    let lastWeek = addDays(start, -7);
    let calString = '';
    for (const [week, cws] of entries) {
        const delta = deltaDays(lastWeek, week);
        calString += '0'.repeat(delta - 7);
        calString += cws;
        lastWeek = week;
    }
    return [start, calString];
}
/**
 * Convert a {@link DateRanges} to a {@link CalString}
 */
export function dateRangesToCalString(ranges, simplified) {
    if (!simplified)
        ranges = simplifyDateRanges(ranges);
    if (!ranges.length)
        return ['1970-01-01', ''];
    const start = 'date' in ranges[0] ? ranges[0].date : ranges[0].start;
    let lastDay = addDays(start, -1);
    let calString = '';
    for (const range of simplifyDateRanges(ranges)) {
        const rangeStart = 'date' in range ? range.date : range.start;
        const rangeLen = 'date' in range ? 1 : deltaDays(range.start, range.end) + 1;
        const rangeEnd = 'date' in range ? range.date : range.end;
        const delta = deltaDays(lastDay, rangeStart);
        calString += '0'.repeat(delta - 1);
        calString += '1'.repeat(rangeLen);
        lastDay = rangeEnd;
    }
    return [start, calString];
}
/**
 * Convert a {@link DateRanges} to a {@link CalWeekString}
 */
export function dateRangesToCalWeekString(ranges) {
    return calStringToCalWeekString(...dateRangesToCalString(ranges));
}
/**
 * Convert a {@link CalString} to a {@link DateRanges}
 */
export function calStringToDateRanges(start, calString) {
    if (!calString.length)
        return [];
    if (calString.match(/^0+$/))
        return [];
    if (start instanceof Date)
        start = start.toISOString().split('T')[0];
    if (calString === '1')
        return [{ date: start }];
    if (calString.match(/^1+$/))
        return [{ start, end: addDays(start, calString.length - 1) }];
    const result = [];
    while (calString) {
        const zeroes = calString.match(/^0*/)[0].length;
        start = addDays(start, zeroes);
        calString = calString.slice(zeroes);
        const ones = calString.match(/^1*/)[0].length;
        if (!ones)
            break;
        if (ones === 1)
            result.push({ date: start });
        else
            result.push({ start, end: addDays(start, ones - 1) });
        start = addDays(start, ones);
        calString = calString.slice(ones);
    }
    return result;
}
/**
 * Minimize a {@link CalString} to optimize space
 */
export function simplifyCalString(start, calString) {
    if (!calString)
        return ['1970-01-01', ''];
    if (start instanceof Date)
        start = start.toISOString().split('T')[0];
    const startZeros = calString.match(/^0+/);
    if (startZeros) {
        const zeroCount = startZeros[0].length;
        calString = calString.slice(zeroCount);
        start = addDays(start, zeroCount);
    }
    const endZeros = calString.match(/0+$/);
    if (endZeros) {
        calString = calString.slice(0, endZeros.index);
    }
    return [start, calString];
}
/**
 * Compute the intersection between two {@link CalString}
 */
export function calStringInter(startA, calStringA, startB, calStringB) {
    if (!calStringA || !calStringB)
        return ['1970-01-01', ''];
    if (startA instanceof Date)
        startA = startA.toISOString().split('T')[0];
    if (startB instanceof Date)
        startB = startB.toISOString().split('T')[0];
    if (startA === startB) {
        const [shorter, longer] = calStringA.length < calStringB.length
            ? [calStringA, calStringB]
            : [calStringB, calStringA];
        const calString = [...shorter]
            // eslint-disable-next-line security/detect-object-injection
            .map((x, i) => (x === '1' && longer[i] === '1' ? '1' : '0'))
            .join('');
        return simplifyCalString(startA, calString);
    }
    if (addDays(startA, calStringA.length) < startB ||
        addDays(startB, calStringB.length) < startA)
        return ['1970-01-01', ''];
    const [startE, startL, calStringE, calStringL] = startA < startB
        ? [startA, startB, calStringA, calStringB]
        : [startB, startA, calStringB, calStringA];
    const delta = deltaDays(startE, startL);
    return calStringInter(startL, calStringE.slice(delta), startL, calStringL);
}
/**
 * Compute the intersection between two {@link CalWeekString}
 */
export function calWeekStringInter(cwsA, cwsB) {
    return Object.fromEntries(Object.entries(cwsA)
        // eslint-disable-next-line security/detect-object-injection
        .filter(([k]) => cwsB[k])
        .map(([k, csA]) => {
        // eslint-disable-next-line security/detect-object-injection
        const csB = cwsB[k];
        return [
            k,
            [...csA]
                .map((x, i) => 
            // eslint-disable-next-line security/detect-object-injection
            x === '1' && csB[i] === '1' ? '1' : '0')
                .join(''),
        ];
    }));
}
/**
 * Convert a {@link CalWeekString} to a {@link DateRanges}
 */
export function calWeekStringToDateRanges(calWeekString) {
    return calStringToDateRanges(...calWeekStringToCalString(calWeekString));
}
/**
 * Convert an arbitrary {@link Dates} to a {@link DateRanges}
 */
export function datesToDateRanges(dates) {
    if (!Array.isArray(dates))
        return calWeekStringToDateRanges(dates);
    if (!dates.length)
        return [];
    if (typeof dates[0] == 'string')
        return calStringToDateRanges(...dates);
    return dates;
}
/**
 * Convert an arbitrary {@link Dates} to a {@link CalString}
 */
export function datesToCalString(dates) {
    if (!Array.isArray(dates))
        return calWeekStringToCalString(dates);
    if (!dates.length)
        return ['1970-01-01', ''];
    if (typeof dates[0] == 'string')
        return dates;
    return dateRangesToCalString(dates);
}
/**
 * Convert an arbitrary {@link Dates} to a {@link CalWeekString}
 */
export function datesToCalWeekString(dates) {
    if (!Array.isArray(dates))
        return dates;
    if (!dates.length)
        return {};
    if (typeof dates[0] == 'string')
        return calStringToCalWeekString(...dates);
    return dateRangesToCalWeekString(dates);
}
/**
 * Convert an arbitrary {@link Dates} to a {@link DateRanges}, {@link CalString} and {@link CalWeekString}
 */
export function datesToAll(dates) {
    if (!Array.isArray(dates)) {
        const calString = calWeekStringToCalString(dates);
        return {
            dateRanges: calStringToDateRanges(...calString),
            calString,
            calWeekString: dates,
        };
    }
    if (!dates.length) {
        return {
            dateRanges: [],
            calString: ['1970-01-01', ''],
            calWeekString: {},
        };
    }
    if (typeof dates[0] == 'string') {
        const calString = dates;
        return {
            dateRanges: calStringToDateRanges(...calString),
            calString,
            calWeekString: calStringToCalWeekString(...calString),
        };
    }
    const dateRanges = dates;
    const calString = dateRangesToCalString(dateRanges);
    return {
        dateRanges,
        calString,
        calWeekString: calStringToCalWeekString(...calString),
    };
}
/**
 * Convert an arbitrary {@link Dates} to a {@link DateRanges}, {@link CalString} or {@link CalWeekString}, whichever is most space-efficient
 */
export function datesToMostEfficient(dates) {
    const { dateRanges, calString, calWeekString } = datesToAll(dates);
    const options = [simplifyDateRanges(dateRanges), calString, calWeekString];
    let minLength = Infinity;
    let bestOption = null;
    for (const option of options) {
        const length = JSON.stringify(option).length;
        if (length < minLength) {
            bestOption = option;
            minLength = length;
        }
    }
    return bestOption;
}
/**
 * Check if a {@link Dates} contains a given date
 */
export function datesHas(dates, date) {
    if (datesIsCalWeekString(dates))
        return calWeekStringHas(dates, date);
    if (datesIsCalString(dates))
        return calStringHas(...dates, date);
    return dateRangesHas(dates, date);
}
/**
 * Iterate over dates in a {@link Dates}
 */
export function iterDates(dates) {
    if (datesIsCalWeekString(dates))
        return iterCalWeekString(dates);
    if (datesIsCalString(dates))
        return iterCalString(...dates);
    return iterDateRanges(dates);
}
/**
 * Convert a {@link Dates} to a set of dates
 */
export function datesToSet(dates) {
    if (datesIsDateRanges(dates)) {
        const result = new Set();
        for (const range of dates) {
            for (const date of iterDatesAB(dateRangeStart(range), dateRangeEnd(range))) {
                result.add(date);
            }
        }
        return result;
    }
    return new Set(iterDates(dates));
}
/**
 * Compute the intersection of a list of {@link Dates}
 */
export function datesInter(sets) {
    if (!sets.length)
        return [];
    if (sets.length === 1)
        return sets[0];
    const calStrings = sets.map(x => datesToCalString(x));
    let inter = calStrings.shift();
    while (calStrings.length) {
        const next = calStrings.shift();
        inter = calStringInter(...inter, ...next);
    }
    return inter;
}
/**
 * Check if a {@link Dates} contains no dates
 */
export function datesIsEmpty(dates) {
    if (datesIsCalString(dates)) {
        return !dates[1].includes('1');
    }
    else if (datesIsCalWeekString(dates)) {
        return !Object.values(dates).some(x => x.includes('1'));
    }
    else {
        return !!dates.length;
    }
}
//# sourceMappingURL=data:application/json;base64,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